import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { AuthApiService } from '../../infrastructure/auth/auth-api.service';
import { LocalStorageService } from '../../infrastructure/storage/local-storage.service';
import { STORAGE_KEYS } from '../../infrastructure/storage/storage-keys';
import { AuthSession, RefreshedTokens, ScopeType } from '../../infrastructure/auth/auth.model';
import { initialsFromFullName } from '../../infrastructure/auth/auth.mapper';

/** Resultado del intento de login que consume el componente. */
export interface LoginResult {
  success: boolean;
  error?: string;
}

const SCOPE_LABELS: Record<ScopeType, string> = {
  GLOBAL: 'Global',
  TENANT: 'Empresa',
  LOCATION: 'Área',
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(AuthApiService);
  private readonly storage = inject(LocalStorageService);
  private readonly router = inject(Router);

  private readonly _session = signal<AuthSession | null>(this.loadSession());

  readonly session = this._session.asReadonly();

  /** Perfil del usuario autenticado (o null). Nombre conservado para los consumidores. */
  readonly currentUser = computed(() => this._session()?.profile ?? null);

  readonly isAuthenticated = computed(() => this._session() !== null);

  readonly scope = computed<ScopeType | null>(() => this._session()?.scope ?? null);

  readonly userFullName = computed(() => this.currentUser()?.fullName ?? '');

  readonly userInitials = computed(() => {
    const name = this.currentUser()?.fullName;
    return name ? initialsFromFullName(name) : '';
  });

  /** Etiqueta de rol a mostrar: el `roleName` que envía el backend (ej. "Super Administrador"). */
  readonly userRoleLabel = computed(() => this.currentUser()?.roleName ?? '');

  /** Alcance legible del usuario (Global / Empresa / Área). */
  readonly scopeLabel = computed(() => {
    const s = this.scope();
    return s ? SCOPE_LABELS[s] : '';
  });

  /** Refresh en curso compartido (single-flight): varios 401 simultáneos usan el mismo. */
  private refresh$: Observable<string> | null = null;

  /**
   * Access token vigente (o null). Lo necesita el stream SSE del tablero
   * (`EventSource` no puede fijar headers → token por `?token=`, §6.9).
   */
  getAccessToken(): string | null {
    return (
      this._session()?.tokens.accessToken ??
      this.storage.get<string>(STORAGE_KEYS.accessToken) ??
      null
    );
  }

  login(username: string, password: string): Observable<LoginResult> {
    return this.api.login(username, password).pipe(
      map((session) => {
        this.persist(session);
        this._session.set(session);
        return { success: true };
      }),
      catchError((err: unknown) => of({ success: false, error: this.toMessage(err) })),
    );
  }

  /**
   * Renueva los tokens una sola vez aunque lleguen varios 401 a la vez.
   * Emite el nuevo access token. Lo consume el `authInterceptor`.
   */
  refreshTokens(): Observable<string> {
    if (this.refresh$) return this.refresh$;

    const refreshToken =
      this._session()?.tokens.refreshToken ??
      this.storage.get<string>(STORAGE_KEYS.refreshToken);

    if (!refreshToken) {
      return throwError(() => new Error('No hay refresh token disponible.'));
    }

    this.refresh$ = this.api.refresh(refreshToken).pipe(
      tap((tokens) => this.applyRefreshedTokens(tokens)),
      map((tokens) => tokens.accessToken),
      finalize(() => (this.refresh$ = null)),
      shareReplay(1),
    );
    return this.refresh$;
  }

  /** Logout iniciado por el usuario: revoca en el servidor (best-effort) y limpia. */
  logout(): void {
    this.api
      .logout()
      .pipe(catchError(() => of(void 0)))
      .subscribe(() => this.forceLogout());
  }

  /** Cierre forzado sin llamada al servidor (sesión ya inválida, ej. refresh fallido). */
  forceLogout(): void {
    this.clearSession();
    this.router.navigate(['/login']);
  }

  private persist(session: AuthSession): void {
    this.storage.set(STORAGE_KEYS.accessToken, session.tokens.accessToken);
    this.storage.set(STORAGE_KEYS.refreshToken, session.tokens.refreshToken);
    this.storage.set(STORAGE_KEYS.session, session);
  }

  /** Aplica el nuevo par de tokens del refresh, conservando profile y scope. */
  private applyRefreshedTokens(tokens: RefreshedTokens): void {
    const current = this._session();
    if (current) {
      const updated: AuthSession = {
        ...current,
        tokens: {
          ...current.tokens,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        },
      };
      this._session.set(updated);
      this.storage.set(STORAGE_KEYS.session, updated);
    }
    this.storage.set(STORAGE_KEYS.accessToken, tokens.accessToken);
    this.storage.set(STORAGE_KEYS.refreshToken, tokens.refreshToken);
  }

  private clearSession(): void {
    this._session.set(null);
    this.storage.remove(STORAGE_KEYS.accessToken);
    this.storage.remove(STORAGE_KEYS.refreshToken);
    this.storage.remove(STORAGE_KEYS.session);
  }

  /**
   * Carga la sesión guardada SOLO si sigue siendo válida (refresh token no
   * expirado y estructura completa). Una sesión vencida o corrupta se descarta
   * y se limpia, para que el guard mande a login de inmediato en vez de dejar
   * entrar con una sesión muerta.
   */
  private loadSession(): AuthSession | null {
    const session = this.storage.get<AuthSession>(STORAGE_KEYS.session);
    if (session && this.isRefreshValid(session)) {
      return session;
    }
    if (session) {
      this.storage.remove(STORAGE_KEYS.accessToken);
      this.storage.remove(STORAGE_KEYS.refreshToken);
      this.storage.remove(STORAGE_KEYS.session);
    }
    return null;
  }

  /** La sesión es revivible mientras el refresh token no haya expirado. */
  private isRefreshValid(session: AuthSession): boolean {
    const expiresAt = session.tokens?.refreshExpiresAt;
    if (!expiresAt) return false;
    const expiryMs = new Date(expiresAt).getTime();
    return Number.isFinite(expiryMs) && expiryMs > Date.now();
  }

  private toMessage(err: unknown): string {
    return err instanceof Error && err.message
      ? err.message
      : 'No se pudo iniciar sesión. Intenta nuevamente.';
  }
}
